/**
 * Realistic skeleton loader shown as `<Suspense>` fallback while the editor
 * chunk loads.  Mirrors the actual 3-column layout with inner placeholder
 * shapes and a CSS shimmer animation.
 */

const shimmerStyle = `
@keyframes skeleton-shimmer {
  0%   { opacity: 0.5 }
  50%  { opacity: 1 }
  100% { opacity: 0.5 }
}
`;

/** Rounded placeholder bar with shimmer. */
function Bar({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <div
      className={`bg-bg-tertiary rounded ${className}`}
      style={{ animation: 'skeleton-shimmer 2s ease-in-out infinite', ...style }}
    />
  );
}

/** Rounded placeholder circle/square with shimmer. */
function Circle({ size, className = '' }: { size: number; className?: string }) {
  return (
    <div
      className={`bg-bg-tertiary rounded-full shrink-0 ${className}`}
      style={{ width: size, height: size, animation: 'skeleton-shimmer 2s ease-in-out infinite' }}
    />
  );
}

// ─── Sub-panels ──────────────────────────────────────────────────────────────

function SidebarSkeleton() {
  return (
    <div className="shrink-0 bg-bg-primary rounded-2xl flex flex-col overflow-hidden" style={{ width: 260 }}>
      {/* Header — logo + preset name */}
      <div className="flex items-center justify-between px-4 py-3.5 shrink-0">
        <Bar className="rounded-md" style={{ width: 80, height: 18 }} />
        <Bar style={{ width: 60, height: 12 }} />
      </div>

      {/* Tab bar */}
      <div className="flex px-3 pb-2 gap-2 border-b border-border-primary shrink-0">
        <Bar className="flex-1 rounded-lg" style={{ height: 32 }} />
        <Bar className="flex-1 rounded-lg" style={{ height: 32 }} />
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 px-3 pt-4 flex flex-col gap-3">
        {/* Section header + add button */}
        <div className="flex items-center justify-between">
          <Bar style={{ width: 40, height: 12 }} />
          <Circle size={24} className="rounded-lg" />
        </div>

        {/* Search input */}
        <Bar className="rounded-lg" style={{ height: 32 }} />

        {/* Asset rows */}
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-2.5" style={{ animationDelay: `${i * 100}ms` }}>
            <div
              className="shrink-0 bg-bg-tertiary rounded-lg"
              style={{ width: 36, height: 36, animation: 'skeleton-shimmer 2s ease-in-out infinite', animationDelay: `${i * 100}ms` }}
            />
            <Bar className="flex-1" style={{ height: 12, animationDelay: `${i * 100}ms` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ToolbarSkeleton() {
  return (
    <div className="shrink-0 flex justify-center">
      <div className="h-12 bg-bg-primary rounded-2xl flex items-center px-2 gap-1">
        {/* 5 aspect ratio buttons */}
        {[30, 34, 28, 30, 34].map((w, i) => (
          <Bar key={i} className="rounded-[10px]" style={{ width: w, height: 32, animationDelay: `${i * 80}ms` }} />
        ))}

        {/* Custom size button */}
        <Bar className="rounded-[10px]" style={{ width: 32, height: 32 }} />

        {/* Divider */}
        <div className="mx-1.5">
          <div className="w-1 h-4 bg-bg-quaternary rounded-[2px]" />
        </div>

        {/* Color swatch */}
        <Bar className="rounded-[10px]" style={{ width: 32, height: 32 }} />

        {/* Divider */}
        <div className="mx-1.5">
          <div className="w-1 h-4 bg-bg-quaternary rounded-[2px]" />
        </div>

        {/* 3 FPS buttons */}
        {[28, 28, 28].map((w, i) => (
          <Bar key={i} className="rounded-[10px]" style={{ width: w, height: 32, animationDelay: `${i * 80}ms` }} />
        ))}
      </div>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="shrink-0 bg-bg-primary rounded-xl overflow-clip">
      <div className="flex items-center gap-2 px-3 h-9">
        {/* Restart button */}
        <div
          className="size-6 rounded-lg bg-bg-secondary shrink-0"
          style={{ animation: 'skeleton-shimmer 2s ease-in-out infinite' }}
        />
        {/* Play button */}
        <div
          className="size-7 rounded-lg bg-bg-tertiary shrink-0"
          style={{ animation: 'skeleton-shimmer 2s ease-in-out infinite', animationDelay: '100ms' }}
        />
        {/* Timecode */}
        <Bar className="shrink-0" style={{ width: 64, height: 12, animationDelay: '200ms' }} />
        {/* Scrubber track */}
        <div className="flex-1 self-stretch" />
        {/* Fullscreen button */}
        <div
          className="size-6 rounded-lg bg-bg-secondary shrink-0"
          style={{ animation: 'skeleton-shimmer 2s ease-in-out infinite' }}
        />
      </div>
    </div>
  );
}

function RightPanelSkeleton() {
  return (
    <div className="shrink-0 bg-bg-primary rounded-2xl flex flex-col overflow-clip" style={{ width: 254 }}>
      {/* Header — avatar + Reset + Export */}
      <div className="flex items-center gap-2 p-2 border-b border-border-primary shrink-0">
        <Circle size={28} />
        <Bar className="flex-1 rounded-lg" style={{ height: 28 }} />
        <Bar className="flex-1 rounded-lg" style={{ height: 28, animationDelay: '100ms' }} />
      </div>

      {/* Section: Scene */}
      <div className="px-3 pt-4 pb-[17px] flex flex-col border-b border-border-primary">
        <Bar style={{ width: 44, height: 13 }} />
        <div className="w-full flex flex-col gap-3 pt-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="flex items-center justify-between">
              <Bar style={{ width: 60 + (i % 2) * 20, height: 11, animationDelay: `${i * 80}ms` }} />
              <Bar className="rounded-md" style={{ width: 100, height: 20, animationDelay: `${i * 80 + 40}ms` }} />
            </div>
          ))}
        </div>
      </div>

      {/* Section: Motion */}
      <div className="px-3 pt-4 pb-[17px] flex flex-col border-b border-border-primary">
        <Bar style={{ width: 48, height: 13, animationDelay: '200ms' }} />
        <div className="w-full flex flex-col gap-3 pt-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center justify-between">
              <Bar style={{ width: 52 + i * 10, height: 11, animationDelay: `${i * 80 + 300}ms` }} />
              <Bar className="rounded-md" style={{ width: 100, height: 20, animationDelay: `${i * 80 + 340}ms` }} />
            </div>
          ))}
        </div>
      </div>

      {/* Section: Easing */}
      <div className="px-3 pt-4 pb-[17px] flex flex-col">
        <Bar style={{ width: 44, height: 13, animationDelay: '500ms' }} />
        <div className="pt-4">
          <Bar className="rounded-lg" style={{ width: '100%', height: 100, animationDelay: '550ms' }} />
        </div>
      </div>
    </div>
  );
}

// ─── Main skeleton ───────────────────────────────────────────────────────────

export default function EditorSkeleton() {
  return (
    <>
      <style>{shimmerStyle}</style>
      <div className="h-screen w-screen text-text-primary flex overflow-hidden gap-4 p-4 bg-bg-quaternary font-sans">
        <div className="flex flex-1 min-h-0 gap-4">
          <SidebarSkeleton />
          <div className="flex-1 flex flex-col min-w-0 min-h-0 gap-4">
            <ToolbarSkeleton />
            {/* Canvas — empty panel */}
            <div className="flex-1 bg-bg-primary rounded-2xl" />
            <TimelineSkeleton />
          </div>
          <RightPanelSkeleton />
        </div>
      </div>
    </>
  );
}
